/**
 * Easter egg aliases.
 *
 * Each key is the canonical egg id (used by SearchScreen to pick the action),
 * each value is every term (any language) that triggers it.
 * Matching ignores case, accents, punctuation and extra spaces,
 * so "Gravidade", "gravidade!" and "GRAVIDADE" are all the same term.
 *
 * Keep aliases specific: the match is on the WHOLE search text, but a very common
 * word would still show the easter egg button on regular searches.
 */
export type EggId =
    | "67"
    | "thanos"
    | "nox"
    | "lumos"
    | "leviosa"
    | "patronus"
    | "avada"
    | "matrix"
    | "snow"
    | "party"
    | "upsideDown"
    | "barrelRoll"
    | "askew"
    | "gravity"
    | "mario";

export const EGG_ALIASES: Record<EggId, string[]> = {
    "67": [
        "67", "six seven", "sixseven", "seis sete", "seissete", "seis siete",
        "six sept", "sechs sieben", "sei sette", "六七", "六十七", "ستة سبعة",
    ],
    thanos: [
        "thanos", "estalo do thanos", "chasquido de thanos", "finger snap", "thanos snap",
        "infinity gauntlet", "manopla do infinito", "guantelete del infinito",
        "gant de linfini", "unendlichkeitshandschuh", "guanto dellinfinito",
        "perfectly balanced", "perfeitamente equilibrado", "サノス", "灭霸", "ثانوس",
    ],
    nox: [
        "nox", "escuridao", "darkness", "oscuridad", "obscurite", "dunkelheit", "oscurita",
        "apagar a luz", "turn off the light",
    ],
    lumos: [
        "lumos", "lumos maxima", "lumos solem", "acender a luz", "turn on the light", "light up",
    ],
    leviosa: [
        "wingardium leviosa", "leviosa", "wingardium", "levitar", "levitacao", "levitacion",
        "levitation", "levitate", "levitieren", "levitazione",
    ],
    patronus: [
        "expecto patronum", "expecto patronus", "patronus", "patronum", "patrono",
        "feitico patrono", "hechizo patronus", "sortilege du patronus", "patronus zauber",
    ],
    avada: [
        "avada kedavra", "avadakedavra", "avada", "kedavra", "maldicao imperdoavel",
        "unforgivable curse", "maldicion imperdonable",
    ],
    matrix: [
        "matrix", "the matrix", "a matrix", "red pill", "blue pill", "pilula vermelha",
        "pilula azul", "pastilla roja", "pilule rouge", "rote pille", "pillola rossa",
        "follow the white rabbit", "siga o coelho branco", "chuva digital", "digital rain",
        "矩阵", "マトリックス",
    ],
    snow: [
        "let it snow", "snow", "neve", "nieve", "neige", "schnee", "fazer nevar", "nevar", "雪",
    ],
    party: [
        "party", "festa", "fiesta", "fete", "feier", "confetti", "confete", "confeti",
        "celebrate", "celebrar", "comemorar", "パーティー", "派对",
    ],
    upsideDown: [
        "upside down", "mundo invertido", "o mundo invertido", "de cabeca para baixo",
        "de cabeca pra baixo", "stranger things", "mundo del reves", "al reves",
        "monde a lenvers", "kopfuber", "sottosopra", "demogorgon", "vecna",
    ],
    barrelRoll: [
        "do a barrel roll", "barrel roll", "faca um barrel roll", "fazer um barrel roll",
        "haz un barrel roll", "faire un tonneau", "mach eine fassrolle", "fassrolle",
        "fai un barrel roll",
    ],
    askew: [
        "askew", "torto", "tortinho", "inclinar a tela", "inclinar tela", "tilted", "crooked",
        "chueco", "inclinado", "de travers", "schief", "storto", "斜め",
    ],
    gravity: [
        "gravity", "gravidade", "gravedad", "gravite", "schwerkraft", "gravita",
        "cair tudo", "maca de newton", "isaac newton", "重力", "جاذبية",
    ],
    mario: [
        "mario", "super mario", "super mario bros", "mario bros", "moeda do mario",
        "mario coin", "moneda de mario", "piece de mario", "mario muenze", "luigi",
        "マリオ", "马里奥",
    ],
};

/** lowercase, no accents, no punctuation, single spaces. */
export function normalizeEggTerm(value: string): string {
    return value
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .replace(/[.,;:!?¿¡"'`´^~()[\]{}]/g, "")
        .replace(/[-_]+/g, " ")
        .replace(/\s+/g, " ")
        .trim();
}

const LOOKUP: Map<string, EggId> = (() => {
    const map = new Map<string, EggId>();
    (Object.keys(EGG_ALIASES) as EggId[]).forEach(id => {
        EGG_ALIASES[id].forEach(alias => {
            const key = normalizeEggTerm(alias);
            if (key && !map.has(key)) map.set(key, id);
        });
    });
    return map;
})();

/** Returns the egg id for a search term, or undefined if the term is not an easter egg. */
export function resolveEggId(term: string): EggId | undefined {
    return LOOKUP.get(normalizeEggTerm(term));
}
